import {
  ADDITION_PATTERN,
  PRISM_PATTERN,
  SIGNED_POWER_PATTERN,
  formatPrismDioptre,
  formatSignedHundredths,
  isQuarterDioptre,
  parseDioptreToHundredths,
} from '@optiflow/shared';
import {
  ACUITY_CATEGORIES,
  AcuityCorrection,
  AcuityDistance,
  DistanceAcuityNotation,
  NearAcuityNotation,
  RefractionKind,
  type EyeExaminationDetail,
  type EyeInput,
  type EyePrescription,
  type PrismBase,
  type SaveEyeExaminationInput,
} from '@optiflow/types';

/**
 * The eye examination form: what the screen holds, what a save sends, and the rules a save is checked against first.
 *
 * Every clinical value stays the string the examiner typed until it is sent; powers are only ever read as integer hundredths, never as floating point. A save sends the whole draft, because the API replaces the draft with what it receives — a field the examiner never touched must still travel.
 */

export type EyeSide = 'right' | 'left';
export type AcuityEye = 'right' | 'left' | 'binocular';
export const EYE_FIELDS = ['sphere', 'cylinder', 'axis', 'add', 'prism', 'prismBase'] as const;
export type EyeField = (typeof EYE_FIELDS)[number];

export interface EyeForm {
  sphere: string;
  cylinder: string;
  axis: string;
  add: string;
  prism: string;
  prismBase: PrismBase | '';
}

export interface AcuityForm {
  right: string;
  left: string;
  binocular: string;
}

export type AcuityKey = `${AcuityDistance}:${AcuityCorrection}`;

/** The six acuity tests, distance first, each through no correction, the current one, then the new refraction. */
export const ACUITY_ROWS: {
  key: AcuityKey;
  distance: AcuityDistance;
  correction: AcuityCorrection;
}[] = [AcuityDistance.DISTANCE, AcuityDistance.NEAR].flatMap((distance) =>
  [AcuityCorrection.UNAIDED, AcuityCorrection.HABITUAL, AcuityCorrection.BEST_CORRECTED].map(
    (correction) => ({ key: `${distance}:${correction}` as AcuityKey, distance, correction }),
  ),
);

export const REFRACTION_KINDS: RefractionKind[] = [
  RefractionKind.HABITUAL,
  RefractionKind.OBJECTIVE,
  RefractionKind.SUBJECTIVE,
];

/** The refraction tables shown on the examination screen. The current correction is hidden from the normal workflow, but an examination that already recorded one still shows it; the form keeps and sends every table either way, so nothing recorded is cleared. */
export function visibleRefractionKinds(
  recorded: readonly { kind: RefractionKind }[],
): RefractionKind[] {
  const hasCurrentCorrection = recorded.some((entry) => entry.kind === RefractionKind.HABITUAL);
  return REFRACTION_KINDS.filter(
    (kind) => kind !== RefractionKind.HABITUAL || hasCurrentCorrection,
  );
}

export interface ExaminationForm {
  /** `YYYY-MM-DDTHH:mm` in the browser's time, as a datetime-local input holds it. */
  examinedAt: string;
  examinerId: string;
  distanceAcuityNotation: DistanceAcuityNotation;
  nearAcuityNotation: NearAcuityNotation;
  reasonForVisit: string;
  clinicalNotes: string;
  acuities: Record<AcuityKey, AcuityForm>;
  refractions: Record<RefractionKind, { right: EyeForm; left: EyeForm }>;
  issuePrescription: boolean;
  prescription: {
    right: EyeForm;
    left: EyeForm;
    pdMeasurementId: string;
    /** `YYYY-MM-DD`, or empty. */
    expiresOn: string;
    notes: string;
  };
}

export const EMPTY_EYE: EyeForm = {
  sphere: '',
  cylinder: '',
  axis: '',
  add: '',
  prism: '',
  prismBase: '',
};

const EMPTY_ACUITY: AcuityForm = { right: '', left: '', binocular: '' };

/** A message for one field: an i18n key and its values. */
export interface FieldError {
  key: string;
  values?: Record<string, string>;
}
export type FormErrors = Record<string, FieldError>;

export const fieldPath = {
  acuity: (key: AcuityKey, eye: AcuityEye) => `acuity.${key}.${eye}`,
  refraction: (kind: RefractionKind, eye: EyeSide, field: EyeField) =>
    `refraction.${kind}.${eye}.${field}`,
  prescription: (eye: EyeSide, field: EyeField) => `prescription.${eye}.${field}`,
};

// ---------------------------------------------------------------------------
// Building the form
// ---------------------------------------------------------------------------

export function emptyExaminationForm(now: Date, examinerId: string): ExaminationForm {
  return {
    examinedAt: toLocalDateTimeInput(now),
    examinerId,
    distanceAcuityNotation: DistanceAcuityNotation.SNELLEN_METRIC,
    nearAcuityNotation: NearAcuityNotation.N_POINT,
    reasonForVisit: '',
    clinicalNotes: '',
    acuities: Object.fromEntries(
      ACUITY_ROWS.map((row) => [row.key, { ...EMPTY_ACUITY }]),
    ) as Record<AcuityKey, AcuityForm>,
    refractions: Object.fromEntries(
      REFRACTION_KINDS.map((kind) => [kind, { right: { ...EMPTY_EYE }, left: { ...EMPTY_EYE } }]),
    ) as ExaminationForm['refractions'],
    issuePrescription: false,
    prescription: {
      right: { ...EMPTY_EYE },
      left: { ...EMPTY_EYE },
      pdMeasurementId: '',
      expiresOn: '',
      notes: '',
    },
  };
}

/** The form for a saved examination, carrying every value the server returned — including the ones nobody will touch. */
export function formFromDetail(detail: EyeExaminationDetail): ExaminationForm {
  const form = emptyExaminationForm(new Date(detail.examinedAt), detail.examinerId ?? '');
  form.distanceAcuityNotation = detail.distanceAcuityNotation;
  form.nearAcuityNotation = detail.nearAcuityNotation;
  form.reasonForVisit = detail.reasonForVisit ?? '';
  form.clinicalNotes = detail.clinicalNotes ?? '';

  for (const acuity of detail.acuities) {
    form.acuities[`${acuity.distance}:${acuity.correction}`] = {
      right: acuity.right ?? '',
      left: acuity.left ?? '',
      binocular: acuity.binocular ?? '',
    };
  }
  for (const refraction of detail.refractions) {
    form.refractions[refraction.kind] = {
      right: eyeFormOf(refraction.right),
      left: eyeFormOf(refraction.left),
    };
  }
  if (detail.prescription) {
    form.issuePrescription = true;
    form.prescription = {
      right: eyeFormOf(detail.prescription.right),
      left: eyeFormOf(detail.prescription.left),
      pdMeasurementId: detail.prescription.pd.measurementId ?? '',
      expiresOn: detail.prescription.expiresOn ?? '',
      notes: detail.prescription.notes ?? '',
    };
  }
  return form;
}

function eyeFormOf(eye: EyePrescription): EyeForm {
  return {
    sphere: eye.sphere ?? '',
    cylinder: eye.cylinder ?? '',
    axis: eye.axis === null ? '' : String(eye.axis),
    add: eye.add ?? '',
    prism: eye.prism ?? '',
    prismBase: eye.prismBase ?? '',
  };
}

/** Copies the subjective refraction into the prescription exactly as written: no calculation, no transposition, no sign change. */
export function copySubjectiveToPrescription(form: ExaminationForm): ExaminationForm {
  const subjective = form.refractions[RefractionKind.SUBJECTIVE];
  return {
    ...form,
    prescription: {
      ...form.prescription,
      right: { ...subjective.right },
      left: { ...subjective.left },
    },
  };
}

export function eyeHasValues(eye: EyeForm): boolean {
  return EYE_FIELDS.some((field) => eye[field].trim() !== '');
}

// ---------------------------------------------------------------------------
// Sending it
// ---------------------------------------------------------------------------

/**
 * The whole draft as the API takes it.
 *
 * Nothing is left out because it looks untouched: the examiner, the notations, every acuity, refraction and prescription value travel on every save. An examination date the examiner did not change is sent back exactly as the server gave it.
 */
export function examinationPayload(
  form: ExaminationForm,
  saved?: { examinedAt: string },
): SaveEyeExaminationInput {
  const examinedAt =
    saved && toLocalDateTimeInput(new Date(saved.examinedAt)) === form.examinedAt
      ? saved.examinedAt
      : new Date(form.examinedAt).toISOString();

  return {
    examinedAt,
    examinerId: form.examinerId || null,
    distanceAcuityNotation: form.distanceAcuityNotation,
    nearAcuityNotation: form.nearAcuityNotation,
    reasonForVisit: textOrNull(form.reasonForVisit),
    clinicalNotes: textOrNull(form.clinicalNotes),
    acuities: ACUITY_ROWS.flatMap(({ key, distance, correction }) => {
      const acuity = form.acuities[key];
      const values = Object.fromEntries(
        (['right', 'left', 'binocular'] as const)
          .filter((eye) => acuity[eye].trim() !== '')
          .map((eye) => [eye, acuity[eye].trim()]),
      );
      return Object.keys(values).length > 0 ? [{ distance, correction, ...values }] : [];
    }),
    refractions: REFRACTION_KINDS.flatMap((kind) => {
      const { right, left } = form.refractions[kind];
      if (!eyeHasValues(right) && !eyeHasValues(left)) return [];
      return [{ kind, ...eyesPayload(right, left) }];
    }),
    prescription: form.issuePrescription
      ? {
          ...eyesPayload(form.prescription.right, form.prescription.left),
          pdMeasurementId: form.prescription.pdMeasurementId || null,
          expiresOn: form.prescription.expiresOn || null,
          notes: textOrNull(form.prescription.notes),
        }
      : null,
  };
}

function eyesPayload(right: EyeForm, left: EyeForm): { right?: EyeInput; left?: EyeInput } {
  const result: { right?: EyeInput; left?: EyeInput } = {};
  const r = eyeInput(right);
  const l = eyeInput(left);
  if (r) result.right = r;
  if (l) result.left = l;
  return result;
}

function eyeInput(eye: EyeForm): EyeInput | undefined {
  const input: EyeInput = {};
  if (eye.sphere.trim()) input.sphere = eye.sphere.trim();
  if (eye.cylinder.trim()) input.cylinder = eye.cylinder.trim();
  // An axis is a whole number of degrees, not an optical power.
  if (eye.axis.trim()) input.axis = Number.parseInt(eye.axis.trim(), 10);
  if (eye.add.trim()) input.add = eye.add.trim();
  if (eye.prism.trim()) input.prism = eye.prism.trim();
  if (eye.prismBase) input.prismBase = eye.prismBase;
  return Object.keys(input).length > 0 ? input : undefined;
}

function textOrNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

// ---------------------------------------------------------------------------
// Checking it
// ---------------------------------------------------------------------------

/** How each notation is written; the same structural patterns the API applies. Nothing says what a value means. */
const ACUITY_FORMATS: Record<DistanceAcuityNotation | NearAcuityNotation, RegExp> = {
  SNELLEN_METRIC: /^\d{1,2}(?:\.\d)?\/\d{1,3}(?:\.\d)?(?:[+-]\d)?$/,
  DECIMAL: /^\d(?:\.\d{1,2})?$/,
  N_POINT: /^N\d{1,2}(?:\.\d)?$/,
};

const AXIS_PATTERN = /^\d{1,3}$/;
/** A clock a few minutes fast is not an examination in the future. */
const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

export function validateExaminationForm(form: ExaminationForm, now: Date = new Date()): FormErrors {
  const errors: FormErrors = {};

  const examinedAt = new Date(form.examinedAt);
  if (!form.examinedAt || Number.isNaN(examinedAt.getTime())) {
    errors['visit.examinedAt'] = { key: 'medical.validation.examinedAt' };
  } else if (examinedAt.getTime() > now.getTime() + FUTURE_TOLERANCE_MS) {
    errors['visit.examinedAt'] = { key: 'medical.validation.examinedAtFuture' };
  }

  for (const { key, distance } of ACUITY_ROWS) {
    const notation =
      distance === AcuityDistance.DISTANCE ? form.distanceAcuityNotation : form.nearAcuityNotation;
    for (const eye of ['right', 'left', 'binocular'] as const) {
      const value = form.acuities[key][eye].trim();
      if (value !== '' && !isAcuityValue(value, notation)) {
        errors[fieldPath.acuity(key, eye)] = {
          key: 'medical.validation.acuity',
          values: { examples: ACUITY_EXAMPLES[notation] },
        };
      }
    }
  }

  for (const kind of REFRACTION_KINDS) {
    for (const eye of ['right', 'left'] as const) {
      validateEye(
        form.refractions[kind][eye],
        (field) => fieldPath.refraction(kind, eye, field),
        errors,
      );
    }
  }

  if (form.issuePrescription) {
    for (const eye of ['right', 'left'] as const) {
      validateEye(form.prescription[eye], (field) => fieldPath.prescription(eye, field), errors);
    }
  }

  return errors;
}

export const ACUITY_EXAMPLES: Record<DistanceAcuityNotation | NearAcuityNotation, string> = {
  SNELLEN_METRIC: '6/6, 6/9-2',
  DECIMAL: '0.8, 1.0',
  N_POINT: 'N5, N8',
};

export function isAcuityValue(
  value: string,
  notation: DistanceAcuityNotation | NearAcuityNotation,
): boolean {
  const normalised = normaliseAcuity(value);
  return (
    (ACUITY_CATEGORIES as readonly string[]).includes(normalised) ||
    ACUITY_FORMATS[notation].test(normalised)
  );
}

export function normaliseAcuity(value: string): string {
  return value.trim().toUpperCase();
}

/** One eye by the rules the server reads every prescription line with; shared with the optical order builder. */
export function validateEye(eye: EyeForm, path: (field: EyeField) => string, errors: FormErrors) {
  const sphere = readPower(eye.sphere, SIGNED_POWER_PATTERN);
  const cylinder = readPower(eye.cylinder, SIGNED_POWER_PATTERN);
  const add = readPower(eye.add, ADDITION_PATTERN);
  const prism = readPower(eye.prism, PRISM_PATTERN);
  const axis = eye.axis.trim();

  if (sphere === 'invalid') errors[path('sphere')] = { key: 'medical.validation.power' };
  else if (sphere !== null && !isQuarterDioptre(sphere))
    errors[path('sphere')] = { key: 'medical.validation.quarterStep' };

  if (cylinder === 'invalid') errors[path('cylinder')] = { key: 'medical.validation.power' };
  else if (cylinder !== null && !isQuarterDioptre(cylinder))
    errors[path('cylinder')] = { key: 'medical.validation.quarterStep' };

  if (add === 'invalid' || (typeof add === 'number' && add <= 0))
    errors[path('add')] = { key: 'medical.validation.addition' };
  else if (add !== null && !isQuarterDioptre(add))
    errors[path('add')] = { key: 'medical.validation.quarterStep' };

  if (prism === 'invalid' || (typeof prism === 'number' && prism <= 0))
    errors[path('prism')] = { key: 'medical.validation.prism' };

  const hasCylinder = typeof cylinder === 'number' && cylinder !== 0;
  if (axis !== '' && (!AXIS_PATTERN.test(axis) || Number.parseInt(axis, 10) > 180)) {
    errors[path('axis')] = { key: 'medical.validation.axis' };
  } else if (hasCylinder && axis === '') {
    errors[path('axis')] = { key: 'medical.validation.cylinderNeedsAxis' };
  } else if (!hasCylinder && axis !== '' && cylinder !== 'invalid') {
    errors[path('axis')] = { key: 'medical.validation.axisNeedsCylinder' };
  }

  if (prism !== null && prism !== 'invalid' && eye.prismBase === '') {
    errors[path('prismBase')] = { key: 'medical.validation.prismBase' };
  }
  if (prism === null && eye.prismBase !== '') {
    errors[path('prism')] = { key: 'medical.validation.baseWithoutPrism' };
  }

  const othersPresent =
    cylinder !== null || axis !== '' || add !== null || prism !== null || eye.prismBase !== '';
  if (sphere === null && othersPresent && !errors[path('sphere')]) {
    errors[path('sphere')] = { key: 'medical.validation.sphereFirst' };
  }
}

/** Integer hundredths, null when empty, or 'invalid' when the text is not a power in the expected form. */
function readPower(raw: string, pattern: RegExp): number | null | 'invalid' {
  const value = raw.trim();
  if (value === '') return null;
  if (!pattern.test(value)) return 'invalid';
  return parseDioptreToHundredths(value) ?? 'invalid';
}

/** On leaving a power field: two decimals and an explicit sign for sphere and cylinder, from integer hundredths. An unreadable value stays exactly as typed. */
export function normalisePower(value: string, kind: 'signed' | 'addition' | 'prism'): string {
  const trimmed = value.trim();
  if (trimmed === '') return '';
  const pattern =
    kind === 'signed'
      ? SIGNED_POWER_PATTERN
      : kind === 'addition'
        ? ADDITION_PATTERN
        : PRISM_PATTERN;
  if (!pattern.test(trimmed)) return value;
  const hundredths = parseDioptreToHundredths(trimmed);
  if (hundredths === null) return value;
  if (kind === 'prism') return formatPrismDioptre(trimmed) ?? value;
  return formatSignedHundredths(hundredths);
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/** A moment as a datetime-local input shows it, in the browser's own time. */
export function toLocalDateTimeInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// ---------------------------------------------------------------------------
// Refusals from the server
// ---------------------------------------------------------------------------

/** The form field a server validation detail names, read against the payload that was sent. Null when it names none. */
export function formFieldForServerField(
  field: string,
  payload: SaveEyeExaminationInput,
): string | null {
  const acuity = /^acuities\.(\d+)\.(right|left|binocular)$/.exec(field);
  if (acuity) {
    const row = payload.acuities?.[Number(acuity[1])];
    return row
      ? fieldPath.acuity(`${row.distance}:${row.correction}`, acuity[2] as AcuityEye)
      : null;
  }

  const refraction =
    /^refractions\.(\d+)\.(right|left)\.(sphere|cylinder|axis|add|prism|prismBase)$/.exec(field);
  if (refraction) {
    const row = payload.refractions?.[Number(refraction[1])];
    return row
      ? fieldPath.refraction(row.kind, refraction[2] as EyeSide, refraction[3] as EyeField)
      : null;
  }

  const prescription =
    /^prescription\.(right|left)\.(sphere|cylinder|axis|add|prism|prismBase)$/.exec(field);
  if (prescription)
    return fieldPath.prescription(prescription[1] as EyeSide, prescription[2] as EyeField);

  const direct: Record<string, string> = {
    examinedAt: 'visit.examinedAt',
    examinerId: 'visit.examinerId',
    'prescription.pdMeasurementId': 'prescription.pdMeasurementId',
    'prescription.expiresOn': 'prescription.expiresOn',
  };
  return direct[field] ?? null;
}
